import { useState } from "react";
import { Button, Popconfirm, Segmented, Table, Tag, message } from "antd";
import { Link } from "react-router-dom";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";

import UploadBricksModal from "./UploadBricksModal";
import useOrganization from "../../hooks/useOrganization";
import { updateBrickCollection } from "../../utils";
import type { Brick, BrickType } from "../../masterImages/types";

interface Props {
  brickCollection: any | null;
  onChange(): void;
}

const BricksList: React.FC<Props> = ({ brickCollection, onChange }) => {
  const organization = useOrganization();
  const [showUpload, setShowUpload] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const deleteBrick = async (brickId: string) => {
    setDeletingId(brickId);
    try {
      await updateBrickCollection(organization, brickCollection._id, {
        bricks: brickCollection.bricks.filter((b: Brick) => b._id !== brickId),
      });
      onChange();
    } catch (e) {
      message.error((e as Error).message);
      onChange();
    } finally {
      setDeletingId(null);
    }
  };

  const setType = async (brickId: string, type: BrickType) => {
    try {
      await updateBrickCollection(organization, brickCollection._id, {
        bricks: brickCollection.bricks.map((b: Brick) => (b._id === brickId ? { ...b, type } : b)),
      });
      onChange();
    } catch (e) {
      message.error((e as Error).message);
      onChange();
    }
  };

  const columns = [
    {
      title: "Image",
      dataIndex: "image",
      key: "image",
      render: (image: string) => <img style={{ width: 75 }} src={image} />,
    },
    {
      title: "Type",
      key: "type",
      render: (_: unknown, brick: Brick) =>
        brick.masterImage ? (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Tag>{brick.type === "half" ? "Half" : "Full"}</Tag>
            <Link
              to={`/${organization}/collections/${brickCollection._id}/masterImages/${brick.masterImage}`}
            >
              <Tag color="blue">Master image</Tag>
            </Link>
          </span>
        ) : (
          <Segmented
            size="small"
            options={[
              { label: "Full", value: "full" },
              { label: "Half", value: "half" },
            ]}
            value={brick.type}
            onChange={(value) => setType(brick._id, value as BrickType)}
          />
        ),
    },
    {
      title: "",
      key: "actions",
      width: 64,
      render: (_: unknown, brick: Brick) =>
        brick.masterImage ? null : (
          <Popconfirm
            title="Delete this brick image?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteBrick(brick._id)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              loading={deletingId === brick._id}
              aria-label="Delete brick image"
            />
          </Popconfirm>
        ),
    },
  ];

  return (
    <>
      <div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setShowUpload(true)}
        />
      </div>
      <Table
        rowKey="_id"
        loading={brickCollection?.bricks == null}
        columns={columns}
        dataSource={brickCollection?.bricks}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, hideOnSinglePage: true }}
      />
      <UploadBricksModal
        brickCollection={brickCollection}
        open={showUpload}
        onReload={onChange}
        onClose={() => {
          setShowUpload(false);
          onChange();
        }}
      />
    </>
  );
};

export default BricksList;
